// Inline translation editing popup.
//
// Mounted once by the translations plugin's init() when `inlineEdit` is on.
// Listens for the custom event dispatched by `@faceup/localization`'s
// `FormattedMessage` when the user Alt/Option-clicks a translation. Renders
// a floating editor anchored to the clicked element and talks to the
// Focalazy dev API.
//
// Auth: cross-site DevTools can't carry the focalazy_session cookie because
// modern browsers block third-party cookies. Instead we ask Focalazy for a
// short-lived Bearer token via a popup to `/dev-auth` (same-origin to
// translations.faceup.com, so it can read the session cookie) and stash the
// token in localStorage. Every `/api/dev/*` request goes out with an
// `Authorization: Bearer <token>` header; on 401 we re-issue the token.
//
// Mirrors the focalazy edit-key modal flow: source language + cs stacked,
// "Is the Czech translation still accurate?" prompt at save time when the
// source was edited and cs wasn't, server-side auto-translation of the
// remaining project languages.

const TRANSLATION_DEBUG_STORAGE_KEY = 'fu-devtools-translations'
const TRANSLATION_TOKEN_STORAGE_KEY = 'fu-devtools-translations-token'
const INLINE_EDIT_EVENT = 'fu-tools:inline-translate'
const POPUP_ID = 'fu-tools-inline-translation-popup'
const DEFAULT_FOCALAZY_BASE_URL = 'https://translations.faceup.com'
const DEFAULT_FOCALAZY_PROJECT = 'monorepo'

type StoredToken = { token: string; expiresAt: number }

const readStoredToken = (): StoredToken | null => {
  try {
    const raw = window.localStorage.getItem(TRANSLATION_TOKEN_STORAGE_KEY)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw) as StoredToken
    // Treat tokens within 60s of expiry as already gone so we re-auth before
    // racing the server's clock.
    if (!parsed?.token || parsed.expiresAt * 1000 < Date.now() + 60_000) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

const storeToken = (value: StoredToken) => {
  try {
    window.localStorage.setItem(TRANSLATION_TOKEN_STORAGE_KEY, JSON.stringify(value))
  } catch {
    /* ignore */
  }
}

const clearStoredToken = () => {
  try {
    window.localStorage.removeItem(TRANSLATION_TOKEN_STORAGE_KEY)
  } catch {
    /* ignore */
  }
}

// Opens the focalazy /dev-auth popup, listens for the postMessage carrying a
// freshly-minted dev token, stores it, and resolves. Rejects (resolves null)
// if the popup is blocked, closed without a token, or times out.
const requestDevToken = (baseUrl: string): Promise<StoredToken | null> => {
  return new Promise(resolve => {
    // Base64-encode the opener origin before putting it on the URL. CloudFront
    // WAF rules in front of translations.faceup.com 403 any URL whose query
    // string contains the literal `localhost` (treated as SSRF), which would
    // break dev origins like http://localhost:3040. The dev-auth page also
    // accepts a legacy plain `origin=` param for backward compat.
    const encodedOrigin = window.btoa(window.location.origin)
    const popupUrl = `${baseUrl}/dev-auth?o=${encodeURIComponent(encodedOrigin)}`
    const popup = window.open(popupUrl, 'fu-tools-dev-auth', 'width=420,height=520')
    if (!popup) {
      resolve(null)
      return
    }
    let settled = false
    const cleanup = () => {
      if (settled) {
        return
      }
      settled = true
      window.removeEventListener('message', onMessage)
      clearInterval(pollClosed)
      clearTimeout(timeout)
    }
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== baseUrl) {
        return
      }
      const data = event.data
      if (!data || data.type !== 'fu-tools:dev-token') {
        return
      }
      const token = typeof data.token === 'string' ? data.token : null
      const expiresAt = typeof data.expiresAt === 'number' ? data.expiresAt : null
      if (!token || !expiresAt) {
        cleanup()
        resolve(null)
        return
      }
      const stored: StoredToken = { token, expiresAt }
      storeToken(stored)
      cleanup()
      resolve(stored)
    }
    // Resolve null if the user closes the popup without authenticating.
    const pollClosed = window.setInterval(() => {
      if (popup.closed) {
        cleanup()
        resolve(null)
      }
    }, 500)
    // Hard ceiling so a forgotten popup doesn't leave an open promise forever.
    const timeout = window.setTimeout(
      () => {
        cleanup()
        resolve(null)
      },
      5 * 60 * 1000
    )
    window.addEventListener('message', onMessage)
  })
}

type DevFetchInit = Omit<RequestInit, 'headers'> & {
  headers?: Record<string, string>
}

// Fetch wrapper that attaches the stored bearer token and, on 401, asks the
// user to re-auth and retries once. Returns the final Response.
const devFetch = async (
  baseUrl: string,
  url: string,
  init: DevFetchInit = {}
): Promise<Response> => {
  const withAuth = async (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = { ...(init.headers ?? {}) }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
    return fetch(url, { ...init, headers, credentials: 'include' })
  }

  const cached = readStoredToken()
  let response = await withAuth(cached?.token ?? null)
  if (response.status !== 401) {
    return response
  }

  // Cached token rejected (or never existed) → drop it, prompt the user, retry.
  clearStoredToken()
  const fresh = await requestDevToken(baseUrl)
  if (!fresh) {
    return response
  }
  response = await withAuth(fresh.token)
  return response
}

type AnchorRect = { x: number; y: number; width: number; height: number }
type InlineEditEventDetail = { id: string; anchor: AnchorRect }

type Settings = {
  inlineEdit?: boolean
  focalazyBaseUrl?: string
  focalazyProjectSlug?: string
}

type LanguagePayload = { value: string; needsTranslation: boolean }
type LoadedPayload = {
  key: string
  sourceLanguage: string
  languages: string[]
  pluralization: boolean
  source: LanguagePayload
  cs: { enabled: true; value: string; needsTranslation: boolean } | { enabled: false }
  skEnabled: boolean
}

const readSettings = (): Settings => {
  try {
    const raw = window.localStorage.getItem(TRANSLATION_DEBUG_STORAGE_KEY)
    return raw ? (JSON.parse(raw) as Settings) : {}
  } catch {
    return {}
  }
}

const closePopup = () => {
  document.getElementById(POPUP_ID)?.remove()
}

const positionPopup = (popup: HTMLElement, anchor: AnchorRect) => {
  // Defer until DOM has dimensions so we can mirror-flip when the popup
  // would otherwise leave the viewport.
  requestAnimationFrame(() => {
    const margin = 8
    const width = popup.offsetWidth || 360
    const height = popup.offsetHeight || 200
    // Below-anchor is the default. Flip above when the popup wouldn't fit
    // below, AND above has more room than below — otherwise leaving it below
    // (with the max-height clamp below) preserves visibility of the source
    // text the user just clicked.
    const spaceBelow = window.innerHeight - (anchor.y + anchor.height) - margin
    const spaceAbove = anchor.y - margin
    let top = anchor.y + anchor.height + margin
    if (height > spaceBelow && spaceAbove > spaceBelow) {
      top = Math.max(margin, anchor.y - height - margin)
    }
    let left = anchor.x
    if (left + width > window.innerWidth - margin) {
      left = window.innerWidth - width - margin
    }
    if (left < margin) {
      left = margin
    }
    popup.style.top = `${Math.round(top + window.scrollY)}px`
    popup.style.left = `${Math.round(left + window.scrollX)}px`
  })
}

const renderShell = (anchor: AnchorRect): HTMLElement => {
  closePopup()
  const popup = document.createElement('div')
  popup.id = POPUP_ID
  // Layout/visual style via Tailwind (mirroring the rest of devtools);
  // top/left land later via positionPopup. The `text-[13px]/[1.5]` +
  // `font-[ui-sans-serif,system-ui,sans-serif]` pin typography to a system
  // stack so the popup looks the same regardless of host page fonts.
  popup.className = [
    'absolute',
    'z-[2147483647]',
    'w-[400px]',
    'max-w-[calc(100vw-16px)]',
    // Hard cap on height so a popup that's still taller than the viewport
    // (large source string, many languages) scrolls internally instead of
    // disappearing past the bottom edge.
    'max-h-[calc(100vh-16px)]',
    'overflow-y-auto',
    'bg-[#0f172a]/95',
    'text-white',
    'border',
    'border-white/5',
    'rounded-[14px]',
    'shadow-2xl',
    'shadow-black/55',
    'p-4',
    'backdrop-blur-md',
    'text-[13px]/[1.5]',
    'font-[ui-sans-serif,system-ui,sans-serif]',
  ].join(' ')
  document.body.appendChild(popup)
  positionPopup(popup, anchor)
  // The shell is appended empty, then innerHTML is swapped to a loading
  // placeholder, then to the much taller editor. Each swap changes height,
  // so the original positionPopup call (based on the empty shell's size)
  // can leave the popup overflowing below the viewport. A ResizeObserver
  // re-runs positioning on every size change so the flip-above decision
  // always reflects the actual content.
  if (typeof ResizeObserver !== 'undefined') {
    const observer = new ResizeObserver(() => positionPopup(popup, anchor))
    observer.observe(popup)
    const originalRemove = popup.remove.bind(popup)
    popup.remove = () => {
      observer.disconnect()
      originalRemove()
    }
  }
  const onDocClick = (event: MouseEvent) => {
    if (!popup.contains(event.target as Node)) {
      window.removeEventListener('mousedown', onDocClick, true)
      window.removeEventListener('keydown', onKey, true)
      closePopup()
    }
  }
  const onKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      window.removeEventListener('mousedown', onDocClick, true)
      window.removeEventListener('keydown', onKey, true)
      closePopup()
    }
  }
  window.addEventListener('mousedown', onDocClick, true)
  window.addEventListener('keydown', onKey, true)
  return popup
}

const headerHtml = (keyId: string) => `
  <div class="flex items-center justify-between mb-2.5 gap-2">
    <code class="text-[#64748b] text-[11px] font-mono overflow-hidden text-ellipsis whitespace-nowrap [direction:rtl]">${escapeHtml(keyId)}</code>
  </div>
`

// Primary cyan action button — same recipe everywhere (Save, Connect,
// Yes/Keep). Building it once keeps the hover ramp and weights in sync.
const primaryButtonClass = [
  'bg-[#22d3ee]',
  'hover:bg-[#67e8f9]',
  'disabled:bg-[#22d3ee]/30',
  'disabled:cursor-not-allowed',
  'text-[#0b1220]',
  'rounded-lg',
  'cursor-pointer',
  'font-semibold',
  'transition-colors',
  'duration-150',
  'border-0',
].join(' ')

// Toast — a small auto-dismissing banner pinned to the bottom-right of the
// viewport. Used to surface post-save status after the popup has closed,
// since the user wouldn't see anything otherwise.
const TOAST_ID = 'fu-tools-translation-toast'
const showToast = (message: string, ttlMs = 4000) => {
  document.getElementById(TOAST_ID)?.remove()
  const toast = document.createElement('div')
  toast.id = TOAST_ID
  toast.textContent = message
  toast.className = [
    'fixed',
    'bottom-4',
    'right-4',
    'z-[2147483647]',
    'max-w-[360px]',
    'bg-[#0f172a]/95',
    'text-white',
    'border',
    'border-white/10',
    'rounded-xl',
    'shadow-2xl',
    'shadow-black/50',
    'backdrop-blur-md',
    'px-4',
    'py-3',
    'text-[12.5px]/[1.45]',
    'font-[ui-sans-serif,system-ui,sans-serif]',
    'transition',
    'duration-200',
    'opacity-0',
    'translate-y-2',
  ].join(' ')
  document.body.appendChild(toast)
  // Force a layout pass before flipping to the visible state so the
  // transition actually runs (rather than the toast appearing fully visible).
  requestAnimationFrame(() => {
    toast.classList.remove('opacity-0', 'translate-y-2')
    toast.classList.add('opacity-100', 'translate-y-0')
  })
  setTimeout(() => {
    if (!toast.isConnected) {
      return
    }
    toast.classList.remove('opacity-100', 'translate-y-0')
    toast.classList.add('opacity-0', 'translate-y-2')
    setTimeout(() => toast.remove(), 250)
  }, ttlMs)
}

// Ghost / secondary action — outlined slate with subtle hover fill.
const secondaryButtonClass = [
  'bg-transparent',
  'hover:bg-white/[0.04]',
  'text-[#cbd5e1]',
  'border',
  'border-white/10',
  'rounded-lg',
  'cursor-pointer',
  'transition-colors',
  'duration-150',
].join(' ')

const renderLoading = (popup: HTMLElement, keyId: string) => {
  popup.innerHTML = `${headerHtml(keyId)}<div class="text-[#94a3b8]">Loading…</div>`
}

const renderLoginPrompt = (
  popup: HTMLElement,
  keyId: string,
  baseUrl: string,
  onConnected?: () => void
) => {
  popup.innerHTML = `
    ${headerHtml(keyId)}
    <div class="text-[#94a3b8] mb-3">Sign in to Focalazy to load this key.</div>
    <button data-action="login" class="${primaryButtonClass} w-full px-3.5 py-2.5 text-[13px]">
      Connect to Focalazy
    </button>
  `
  popup.querySelector<HTMLButtonElement>('[data-action="login"]')?.addEventListener('click', () => {
    // Opening the popup must be a direct user-gesture descendant or it gets
    // blocked. Trigger it from the click handler, not after an async hop.
    void requestDevToken(baseUrl).then(token => {
      if (!token) {
        return
      }
      onConnected?.()
    })
  })
}

const renderError = (popup: HTMLElement, keyId: string, message: string) => {
  popup.innerHTML = `${headerHtml(keyId)}<div class="text-[#fb7185]">${escapeHtml(message)}</div>`
}

const fieldBlockHtml = (
  lang: string,
  value: string,
  flag: { needsTranslation: boolean; label: string; aiTranslatable?: boolean }
) => {
  const pendingPill = flag.needsTranslation
    ? `<span title="Needs review" class="inline-block w-1.5 h-1.5 rounded-full bg-[#fbbf24] shadow-[0_0_6px_rgba(251,191,36,0.6)]"></span>`
    : ''
  const aiButton = flag.aiTranslatable
    ? `<button type="button" data-action="ai-translate" data-target-lang="${escapeHtml(lang)}"
        title="Translate with AI from the source field"
        class="ml-auto bg-transparent hover:bg-[#22d3ee]/10 text-[#22d3ee] border border-[#22d3ee]/30 hover:border-[#22d3ee]/60 rounded-md px-2 py-0.5 cursor-pointer text-[10px] font-semibold tracking-[0.06em] normal-case transition-colors duration-150">✦ AI translate</button>`
    : ''
  // Plain <div> wrapper (not <label>) — wrapping the whole field block in a
  // <label> made clicking *anywhere* in the header row synthesize a click on
  // the contained form control, which then bubbled in a way that looked like
  // the AI-translate button was firing from a header-row click. With a plain
  // div the only thing that triggers the button is the button itself.
  return `
    <div class="block mt-2.5">
      <div class="flex items-center gap-1.5 text-[#94a3b8] text-[10px] uppercase tracking-[0.12em] font-semibold mb-1.5">
        <span>${escapeHtml(flag.label)}</span>${pendingPill}${aiButton}
      </div>
      <textarea data-field="lang-${escapeHtml(lang)}" rows="3"
        class="w-full box-border bg-[#020617]/65 text-[#e2e8f0] border border-white/[0.06] rounded-lg px-3 py-2.5 text-[13px]/[1.5] resize-y outline-none transition-[border-color,box-shadow] duration-150 focus:border-[#22d3ee] focus:shadow-[0_0_0_3px_rgba(34,211,238,0.15)]">${escapeHtml(value)}</textarea>
    </div>
  `
}

const renderEditor = (
  popup: HTMLElement,
  payload: LoadedPayload,
  options: { baseUrl: string; projectSlug: string }
) => {
  const csEnabled = payload.cs.enabled
  const initialSource = payload.source.value
  const initialCs = csEnabled ? payload.cs.value : ''

  popup.innerHTML = `
    ${headerHtml(payload.key)}
    ${fieldBlockHtml(payload.sourceLanguage, initialSource, {
      needsTranslation: payload.source.needsTranslation,
      label: payload.sourceLanguage,
    })}
    ${
      csEnabled
        ? fieldBlockHtml('cs', initialCs, {
            needsTranslation: (payload.cs as { needsTranslation: boolean }).needsTranslation,
            label: 'cs',
            aiTranslatable: true,
          })
        : ''
    }
    <div data-field="status" class="mt-2.5 text-[12px] text-[#94a3b8] min-h-4"></div>
    <div class="flex gap-2 mt-2.5">
      <a data-action="open" target="_blank" rel="noopener noreferrer"
        href="${options.baseUrl}/projects/${options.projectSlug}/translations?key=${encodeURIComponent(payload.key)}"
        class="flex-1 text-center bg-[#334155]/60 hover:bg-[#334155]/90 text-[#e2e8f0] border border-white/5 rounded-lg px-3.5 py-2.5 text-[13px] font-medium no-underline transition-colors duration-150">Open in Focalazy</a>
      <button data-action="save" class="${primaryButtonClass} flex-1 px-3.5 py-2.5 text-[13px]">Save</button>
    </div>
    <div data-field="confirm" data-state="closed" class="overflow-hidden rounded-[10px] bg-[#020617]/40 border border-transparent transition-all duration-200 ease-out max-h-0 opacity-0 mt-0 p-0 data-[state=open]:max-h-[260px] data-[state=open]:opacity-100 data-[state=open]:border-white/[0.06] data-[state=open]:mt-3 data-[state=open]:p-3">
      <div class="font-semibold mb-1.5 text-[13.5px]">Is the Czech translation still accurate?</div>
      <div data-field="confirm-desc" class="text-[#94a3b8] text-[12px] mb-2.5 leading-[1.5]"></div>
      <div class="flex gap-1.5 flex-wrap">
        <button data-action="confirm-cancel" class="${secondaryButtonClass} flex-1 px-2.5 py-1.5 text-[12px]">Cancel</button>
        <button data-action="confirm-flag" class="${secondaryButtonClass} flex-1 px-2.5 py-1.5 text-[12px]">No - needs re-translation</button>
        <button data-action="confirm-keep" class="${primaryButtonClass} flex-1 px-2.5 py-1.5 text-[12px]">Yes - keep unchanged</button>
      </div>
    </div>
  `

  const sourceTa = popup.querySelector<HTMLTextAreaElement>(
    `[data-field="lang-${payload.sourceLanguage}"]`
  )
  const csTa = csEnabled ? popup.querySelector<HTMLTextAreaElement>('[data-field="lang-cs"]') : null
  const status = popup.querySelector<HTMLElement>('[data-field="status"]')
  const saveBtn = popup.querySelector<HTMLButtonElement>('[data-action="save"]')
  const confirmBox = popup.querySelector<HTMLElement>('[data-field="confirm"]')
  const confirmDesc = popup.querySelector<HTMLElement>('[data-field="confirm-desc"]')

  sourceTa?.focus()
  if (sourceTa) {
    const length = sourceTa.value.length
    sourceTa.setSelectionRange(length, length)
  }

  const sourceEdited = () => (sourceTa?.value ?? '') !== initialSource
  const csEditedLocal = () => csEnabled && (csTa?.value ?? '') !== initialCs
  const hasChanges = () => sourceEdited() || csEditedLocal()

  // Mirrors edit-key-modal.tsx shouldAskCsConfirmOnSave: ask when cs is in the
  // project, the user hasn't touched cs themselves, and either the source was
  // edited or cs is already flagged as needing review.
  const shouldAskCs = () =>
    csEnabled &&
    !csEditedLocal() &&
    (sourceEdited() || ((payload.cs as { needsTranslation: boolean }).needsTranslation ?? false))

  const setStatus = (message: string, color: string) => {
    if (!status) {
      return
    }
    status.style.color = color
    status.textContent = message
  }

  // True when clicking Save would actually do something: the user typed
  // somewhere, OR cs is flagged stale and we'd surface the confirm prompt.
  const canSave = () => hasChanges() || shouldAskCs()

  const refreshSaveButton = () => {
    if (!saveBtn) {
      return
    }
    saveBtn.disabled = !canSave()
  }

  const setSaving = (saving: boolean, label = 'Save') => {
    if (!saveBtn) {
      return
    }
    // While the request is in flight, force-disabled. Otherwise let
    // `canSave()` decide so a no-op state grays the button out.
    saveBtn.disabled = saving || !canSave()
    saveBtn.textContent = saving ? 'Saving…' : label
  }

  // Visibility toggled via `data-state` attribute (not inline style) so the
  // Tailwind `data-[state=open]:*` variants transition the box in/out
  // smoothly — height, opacity, border, padding, margin all interpolate
  // over 200ms. Inline `style.display` would have hidden it instantly with
  // no animation and lost to the class-defined `max-h-0` when both applied.
  const showConfirm = () => {
    if (!confirmBox || !confirmDesc) {
      return
    }
    // Wording matches focalazy's edit-key-modal cs-confirm prompt exactly so
    // the popup and the full editor read the same to a translator who's
    // seen one and lands on the other.
    const skSuffix = payload.skEnabled
      ? ' Slovak follows Czech and will be updated the same way.'
      : ''
    confirmDesc.textContent = sourceEdited()
      ? `You edited the source copy, which usually means Czech needs a fresh review.${skSuffix}`
      : `Czech is currently flagged as needing review.${skSuffix}`
    confirmBox.setAttribute('data-state', 'open')
  }
  const hideConfirm = () => {
    if (confirmBox) {
      confirmBox.setAttribute('data-state', 'closed')
    }
  }

  const submit = async (keepUnchangedLanguages: string[] = []) => {
    if (!sourceTa) {
      return
    }
    setSaving(true)
    setStatus('', '#6F91B7')

    const valuesPayload: Record<string, string> = {
      [payload.sourceLanguage]: sourceTa.value,
    }
    if (csEnabled && csTa) {
      valuesPayload['cs'] = csTa.value
    }

    const userTouchedLanguages: string[] = []
    if (sourceEdited()) {
      userTouchedLanguages.push(payload.sourceLanguage)
    }
    if (csEditedLocal()) {
      userTouchedLanguages.push('cs')
    }

    try {
      const response = await devFetch(
        options.baseUrl,
        `${options.baseUrl}/api/dev/translations/${encodeURIComponent(options.projectSlug)}/${encodeURIComponent(payload.key)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            values: valuesPayload,
            keepUnchangedLanguages,
            userTouchedLanguages,
          }),
        }
      )
      if (response.status === 401) {
        renderLoginPrompt(popup, payload.key, options.baseUrl)
        return
      }
      if (!response.ok) {
        const detail = (await response.json().catch(() => null)) as { error?: string } | null
        setStatus(detail?.error ?? `Save failed (${response.status})`, '#fb7185')
        setSaving(false)
        return
      }
      // Tell the host app to re-pull the translations bundle (it listens
      // for this event via packages/localization's TranslationEditProvider →
      // LanguageProvider in akutan). With the bundle refetched in
      // ?unreleased=true mode, the new copy lights up on the page without a
      // hard reload.
      window.dispatchEvent(new CustomEvent('focalazy:refresh-translations'))
      showToast('Saved. Other languages auto-translating in the background…')
      hideConfirm()
      closePopup()
      return
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Network error', '#fb7185')
      setSaving(false)
    }
  }

  const onSaveClick = () => {
    // Defensive — button is disabled in this state, but guard against
    // synthesized clicks / keyboard shortcuts.
    if (!canSave()) {
      return
    }
    if (shouldAskCs()) {
      showConfirm()
      return
    }
    void submit()
  }

  // Sync the disabled state to whatever the user has typed so far.
  refreshSaveButton()
  sourceTa?.addEventListener('input', refreshSaveButton)
  csTa?.addEventListener('input', refreshSaveButton)

  saveBtn?.addEventListener('click', onSaveClick)
  popup
    .querySelector<HTMLButtonElement>('[data-action="confirm-cancel"]')
    ?.addEventListener('click', hideConfirm)
  popup
    .querySelector<HTMLButtonElement>('[data-action="confirm-flag"]')
    ?.addEventListener('click', () => {
      hideConfirm()
      void submit()
    })
  popup
    .querySelector<HTMLButtonElement>('[data-action="confirm-keep"]')
    ?.addEventListener('click', () => {
      const targets = ['cs', ...(payload.skEnabled ? ['sk'] : [])]
      void submit(targets)
    })

  // ✦ AI translate buttons — translate the (possibly-unsaved) source value
  // into the target language and drop the result into that lang's textarea.
  popup.querySelectorAll<HTMLButtonElement>('[data-action="ai-translate"]').forEach(btn => {
    btn.addEventListener('click', async event => {
      // Belt-and-braces — the button is now outside any <label>, but
      // stopping propagation makes sure no surrounding wrapper (now or in
      // future tweaks) can interpret the click as a textarea focus or any
      // other side effect.
      event.preventDefault()
      event.stopPropagation()
      const targetLang = btn.getAttribute('data-target-lang')
      if (!targetLang) {
        return
      }
      const targetTa = popup.querySelector<HTMLTextAreaElement>(`[data-field="lang-${targetLang}"]`)
      const sourceValue = sourceTa?.value ?? ''
      if (!sourceValue.trim()) {
        setStatus('Source is empty — add source copy first.', '#fb7185')
        return
      }
      const originalLabel = btn.textContent
      btn.disabled = true
      btn.textContent = '✦ Translating…'
      setStatus('', '#94a3b8')
      try {
        const response = await devFetch(
          options.baseUrl,
          `${options.baseUrl}/api/dev/translations/${encodeURIComponent(options.projectSlug)}/${encodeURIComponent(payload.key)}/ai`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              targetLanguage: targetLang,
              sourceOverride: [{ pluralForm: '', value: sourceValue }],
            }),
          }
        )
        if (!response.ok) {
          const detail = (await response.json().catch(() => null)) as { error?: string } | null
          setStatus(detail?.error ?? `AI translation failed (${response.status})`, '#fb7185')
          return
        }
        const body = (await response.json()) as {
          values: { pluralForm: string; value: string }[]
        }
        const suggestion = body.values?.find(v => v.pluralForm === '')?.value ?? ''
        if (!suggestion) {
          setStatus('No translation generated.', '#fb7185')
          return
        }
        if (targetTa) {
          targetTa.value = suggestion
          // Programmatic `.value = …` doesn't fire `input`, so the listener
          // that toggles the Save button's disabled state never runs and the
          // user sees a freshly-filled field with no way to commit it.
          // Dispatch the event so the same path the user's typing takes
          // re-evaluates `canSave()`.
          targetTa.dispatchEvent(new Event('input', { bubbles: true }))
        }
        setStatus(`✦ AI suggestion filled in for ${targetLang}.`, '#34d399')
      } catch (error) {
        setStatus(error instanceof Error ? error.message : 'Network error', '#fb7185')
      } finally {
        btn.disabled = false
        if (originalLabel) {
          btn.textContent = originalLabel
        }
      }
    })
  })

  const onKey = (event: KeyboardEvent) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault()
      onSaveClick()
    }
  }
  sourceTa?.addEventListener('keydown', onKey)
  csTa?.addEventListener('keydown', onKey)
}

const escapeHtml = (text: string): string =>
  text.replace(/[&<>"']/g, c =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;'
  )

const openPopup = async (detail: InlineEditEventDetail) => {
  const settings = readSettings()
  const baseUrl = settings.focalazyBaseUrl?.trim().replace(/\/$/, '') || DEFAULT_FOCALAZY_BASE_URL
  const projectSlug = settings.focalazyProjectSlug?.trim() || DEFAULT_FOCALAZY_PROJECT
  const popup = renderShell(detail.anchor)
  renderLoading(popup, detail.id)

  try {
    const response = await devFetch(
      baseUrl,
      `${baseUrl}/api/dev/translations/${encodeURIComponent(projectSlug)}/${encodeURIComponent(detail.id)}`
    )
    if (response.status === 401) {
      // After the user signs in, re-enter openPopup with the same anchor so
      // they don't have to re-click the source element.
      renderLoginPrompt(popup, detail.id, baseUrl, () => {
        void openPopup(detail)
      })
      return
    }
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as { error?: string } | null
      renderError(popup, detail.id, body?.error ?? `Failed to load (${response.status})`)
      return
    }
    const body = (await response.json()) as LoadedPayload
    if (body.pluralization) {
      renderError(popup, detail.id, 'Pluralized keys must be edited in the full Focalazy editor.')
      return
    }
    renderEditor(popup, body, { baseUrl, projectSlug })
  } catch (error) {
    renderError(popup, detail.id, error instanceof Error ? error.message : 'Network error')
  }
}

let installed = false

export const installInlineTranslationPopup = () => {
  if (installed || typeof window === 'undefined') {
    return
  }
  installed = true
  window.addEventListener(INLINE_EDIT_EVENT, event => {
    const settings = readSettings()
    if (!settings.inlineEdit) {
      return
    }
    const detail = (event as CustomEvent<InlineEditEventDetail>).detail
    if (!detail) {
      return
    }
    void openPopup(detail)
  })
}
